import { SelectProps, Typography } from "@mui/material";
import { Select } from "components/Select";
import {
  FieldPath,
  FieldValues,
  get,
  UseControllerProps,
  useController,
} from "react-hook-form";

type FormSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = Omit<SelectProps, "onChange"> &
  UseControllerProps<TFieldValues, TName> & {
    options: Array<{
      label: string;
      value: string | number | boolean;
    }>;
    placeholder?: string;
    required?: boolean;
  };

export const FormSelect = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  defaultValue,
  shouldUnregister,
  options,
  disabled,
  readOnly,
  multiple,
  label,
  placeholder,
  ...rest
}: FormSelectProps<TFieldValues, TName>) => {
  const {
    field: { value, ref, ...field },
    formState,
  } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const touched = get(formState.touchedFields, name) || formState.isSubmitted;
  const errorMsg = touched ? get(formState.errors, name)?.message : null;

  return (
    <Select
      {...field}
      {...rest}
      label={label}
      errorMsg={errorMsg}
      inputProps={{ id: name, ref: ref }}
      value={value ?? (multiple ? [] : "")}
      disabled={disabled}
      readOnly={readOnly}
      multiple={multiple}
      displayEmpty
      options={options}
      renderValue={(selected: unknown) => {
        if (selected === undefined || selected === null || selected === "") {
          return <Typography sx={{ opacity: 0.42 }}>{placeholder}</Typography>;
        }
        return options.find((x) => x.value === selected)?.label;
      }}
    ></Select>
  );
};
